import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormField } from '@/services/FormService';
import { cn } from '@/lib/utils';

interface UniversalFormFieldProps {
  field: FormField;
  value: any;
  error?: string;
  onChange: (value: any) => void;
  disabled?: boolean;
  className?: string;
}

export const UniversalFormField: React.FC<UniversalFormFieldProps> = ({
  field,
  value,
  error,
  onChange,
  disabled = false,
  className,
}) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let newValue: any = e.target.value;
    
    if (field.type === 'number' && 'valueAsNumber' in e.target) {
      newValue = (e.target as HTMLInputElement).valueAsNumber;
    }
    
    onChange(newValue);
  };

  const renderField = () => {
    switch (field.type) {
      case 'textarea':
        return (
          <Textarea
            value={value || ''}
            onChange={handleInputChange}
            placeholder={field.placeholder}
            disabled={disabled}
            className={cn(error && 'border-destructive')}
          />
        );

      case 'select':
        return (
          <Select
            value={value || ''}
            onValueChange={onChange}
            disabled={disabled}
          >
            <SelectTrigger className={cn(error && 'border-destructive')}>
              <SelectValue placeholder={field.placeholder || `Select ${field.label.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'file':
        return (
          <Input
            type="file"
            onChange={(e) => onChange(e.target.files)}
            disabled={disabled}
            className={cn(error && 'border-destructive')}
            multiple
          />
        );

      default:
        return (
          <Input
            type={field.type}
            value={value || ''}
            onChange={handleInputChange}
            placeholder={field.placeholder}
            disabled={disabled}
            min={field.min}
            max={field.max}
            step={field.step}
            className={cn(error && 'border-destructive')}
          />
        );
    }
  };

  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={field.name} className="text-sm font-medium">
        {field.label}
        {field.required && <span className="text-destructive ml-1">*</span>}
      </Label>
      {renderField()}
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  );
};